import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useStore, threadIdFor } from '../lib/store';
import { wsClient } from '../lib/ws';
import { startCall } from '../lib/rtc';

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}
const TONES = ['clay', 'water', 'sage', 'plum', 'dune'] as const;
function toneFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 20 20 0 0 1-8.6-3.1 20 20 0 0 1-6.2-6.1A20 20 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L7.9 9.4a16 16 0 0 0 6 6l1.1-1.2a2 2 0 0 1 2.1-.4c.8.3 1.6.5 2.5.6A2 2 0 0 1 22 16.9z" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18 L18 6 M18 6 L11 6 M18 6 L18 13" />
    </svg>
  );
}
function PlusSmallIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function dayLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return 'Today';
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}
function timeLabel(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function Chat() {
  const nav = useNavigate();
  const { id: contactId = '' } = useParams();
  const user = useStore((s) => s.user);
  const contact = useStore((s) => s.contacts.find((c) => c.id === contactId));
  const setThread = useStore((s) => s.setThreadMessages);
  const setActive = useStore((s) => s.setActiveCall);

  const threadId = user ? threadIdFor(user.id, contactId) : '';
  const msgs = useStore((s) => (threadId ? s.messages[threadId] : undefined)) ?? [];
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!contactId || !user) return;
    api.threadMessages(contactId).then((m) => setThread(threadIdFor(user.id, contactId), m)).catch(() => {});
  }, [contactId, setThread, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [msgs.length]);

  useEffect(() => {
    if (!msgs.length) return;
    const last = msgs[msgs.length - 1];
    if (last.senderId === contactId) {
      wsClient.send({ type: 'chat.read', contactId, upTo: last.createdAt });
    }
  }, [msgs, contactId]);

  function send() {
    const body = text.trim();
    if (!body) return;
    const clientId = crypto.randomUUID();
    wsClient.send({ type: 'chat.send', clientId, to: contactId, body });
    setText('');
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadErr('Only images are supported for now.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadErr('Images must be under 8 MB.');
      return;
    }
    setUploading(true);
    setUploadErr(null);
    try {
      const { id } = await api.uploadAttachment(file);
      const clientId = crypto.randomUUID();
      wsClient.send({
        type: 'chat.send',
        clientId,
        to: contactId,
        body: text.trim(),
        attachmentId: id,
      });
      setText('');
    } catch (err: any) {
      setUploadErr(err?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function call() {
    try {
      const h = await startCall(contactId);
      setActive(h.callId);
      nav(`/call/${h.callId}`);
    } catch (e: any) {
      alert('Could not start call: ' + (e.message || e));
    }
  }

  const ordered = useMemo(() => [...msgs].sort((a, b) => a.createdAt - b.createdAt), [msgs]);

  // Group by day
  const grouped = useMemo(() => {
    const out: { day: string; items: typeof ordered }[] = [];
    let lastDay = '';
    for (const m of ordered) {
      const d = dayLabel(m.createdAt);
      if (d !== lastDay) {
        out.push({ day: d, items: [] });
        lastDay = d;
      }
      out[out.length - 1].items.push(m);
    }
    return out;
  }, [ordered]);

  return (
    <div className="app">
      <header className="chat-header">
        <Link to="/" className="back" aria-label="Back">
          <BackIcon />
        </Link>
        <div className="who-block">
          <div className={`avatar sm`} data-tone={contact ? toneFor(contact.id) : 'clay'}>
            {contact ? initials(contact.displayName) : '…'}
            {contact && <span className={`dot sm ${contact.online ? 'online' : ''}`} />}
          </div>
          <div className="who-text">
            <span className="name">{contact?.displayName ?? '…'}</span>
            <span className={`status ${contact?.online ? 'online' : ''}`}>
              {contact?.online ? 'Online now' : 'Offline'}
            </span>
          </div>
        </div>
        <button className="call-btn" onClick={call} aria-label="Call">
          <PhoneIcon />
        </button>
      </header>

      <div className="chat">
        <div className="chat-log" ref={scrollRef}>
          {grouped.map((g) => (
            <div key={g.day} className="stack gap-2">
              <div className="day-divider">{g.day}</div>
              {g.items.map((m, idx) => {
                const isMine = m.senderId === user?.id;
                const next = g.items[idx + 1];
                const isLastInStreak = !next || next.senderId !== m.senderId;
                const isImage = m.kind === 'image' && m.attachment;
                return (
                  <div key={m.id} className="stack gap-1">
                    {isImage ? (
                      <a
                        className={`bubble image ${isMine ? 'mine' : 'theirs'}`}
                        href={`/api/attachment/${m.attachment!.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={`/api/attachment/${m.attachment!.id}`} alt="" loading="lazy" />
                        {m.body && <div className="caption">{m.body}</div>}
                      </a>
                    ) : (
                      <div className={`bubble ${isMine ? 'mine' : 'theirs'}`}>{m.body}</div>
                    )}
                    {isLastInStreak && (
                      <div className={`bubble-meta ${isMine ? 'mine' : 'theirs'}`}>
                        {timeLabel(m.createdAt)}
                        {isMine && m.readAt ? ' · Read' : isMine && m.deliveredAt ? ' · Delivered' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {ordered.length === 0 && (
            <div className="center muted small" style={{ padding: 'var(--s-8) var(--s-5)' }}>
              Nothing here yet — say hello.
            </div>
          )}
        </div>

        {uploadErr && (
          <div className="composer-error" role="alert">
            {uploadErr}
          </div>
        )}
        <div className="composer">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onPickFile}
          />
          <button
            className="plus"
            aria-label="Attach photo"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <PlusSmallIcon />
          </button>
          <input
            placeholder={
              uploading ? 'Sending photo…' : `Message ${contact?.displayName ?? ''}…`
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            disabled={uploading}
          />
          <button
            className="send"
            onClick={send}
            disabled={!text.trim() || uploading}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
}
